/**
 * SalesforceForm Component
 * Salesforce-specific credential form (Instance URL, Client ID, Client Secret)
 */

import React, { useState } from 'react';
import { validateForm, salesforceValidationSchema, hasErrors } from '../../../utils/validation';

interface SalesforceFormProps {
  initialValues: Record<string, any>;
  onSubmit: (credentials: Record<string, any>) => void;
  onBack: () => void;
}

interface SalesforceCredentials {
  instanceUrl: string;
  clientId: string;
  clientSecret: string;
}

export const SalesforceForm: React.FC<SalesforceFormProps> = ({
  initialValues,
  onSubmit,
  onBack,
}) => {
  const [formData, setFormData] = useState<SalesforceCredentials>({
    instanceUrl: initialValues.instanceUrl || '',
    clientId: initialValues.clientId || '',
    clientSecret: initialValues.clientSecret || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showClientSecret, setShowClientSecret] = useState(false);

  const handleChange = (field: keyof SalesforceCredentials, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm(formData, salesforceValidationSchema);
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-base font-extrabold text-gray-900 mb-1 tracking-tight">
          Salesforce Connection Details
        </h3>
        <p className="text-gray-600 mb-6">
          Enter your Salesforce OAuth credentials for API access.
        </p>
      </div>

      {/* Instance URL */}
      <div>
        <label className="lbl">
          Instance URL <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.instanceUrl}
          onChange={(e) => handleChange('instanceUrl', e.target.value)}
          placeholder="https://yourcompany.salesforce.com"
          className={`inp ${errors.instanceUrl ? 'err' : ''}`}
        />
        {errors.instanceUrl && (
          <p className="hint text-red-600">{errors.instanceUrl}</p>
        )}
        <p className="hint">
          Your Salesforce instance URL (e.g., https://na1.salesforce.com)
        </p>
      </div>

      {/* Client ID */}
      <div>
        <label className="lbl">
          Client ID (Consumer Key) <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.clientId}
          onChange={(e) => handleChange('clientId', e.target.value)}
          placeholder="3MVG9..."
          className={`inp ${errors.clientId ? 'err' : ''}`}
        />
        {errors.clientId && (
          <p className="hint text-red-600">{errors.clientId}</p>
        )}
        <p className="hint">
          OAuth Consumer Key from your Connected App
        </p>
      </div>

      {/* Client Secret */}
      <div>
        <label className="lbl">
          Client Secret (Consumer Secret) <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showClientSecret ? 'text' : 'password'}
            value={formData.clientSecret}
            onChange={(e) => handleChange('clientSecret', e.target.value)}
            placeholder="Enter your client secret"
            className={`inp pr-10 ${errors.clientSecret ? 'err' : ''}`}
          />
          <button
            type="button"
            onClick={() => setShowClientSecret(!showClientSecret)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showClientSecret ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            )}
          </button>
        </div>
        {errors.clientSecret && (
          <p className="hint text-red-600">{errors.clientSecret}</p>
        )}
        <p className="hint">
          OAuth Consumer Secret from your Connected App
        </p>
      </div>

      {/* Info Box */}
      <div className="note">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-sm text-gray-600">
            <p className="font-medium mb-1">How to create a Connected App:</p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>Log in to Salesforce Setup</li>
              <li>Navigate to App Manager → New Connected App</li>
              <li>Enable OAuth Settings</li>
              <li>Add OAuth scopes (Full access, Refresh token)</li>
              <li>Copy the Consumer Key and Consumer Secret</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-4">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          Back
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors font-medium"
        >
          Next: Test Connection
        </button>
      </div>
    </form>
  );
};
